// NEW_FILE_JUSTIFIED: Dependant card sub-component — extracted to keep DependantsSection under 200 lines
import { useCallback, useRef, useState } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: components in insurance/ need convex api
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Pencil, Trash2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex _generated is the documented exception
import type { IDependantCardProps, TDependantRelationship } from '@/@types/dependants';

const RELATIONSHIP_LABELS: Record<TDependantRelationship, string> = {
  spouse: 'Spouse',
  child: 'Child',
  parent: 'Parent',
  sibling: 'Sibling',
  other: 'Other',
};

function DependantDocumentUpload({
  dependantId,
  onUpload,
}: {
  dependantId: Id<'dependants'>;
  onUpload: IDependantCardProps['onUploadDocument'];
}) {
  const [isUploading, setIsUploading] = useState(false);
  const createUploadUrl = useMutation(api.storage.createUploadUrl);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setIsUploading(true);
      try {
        const uploadUrl = await createUploadUrl({});
        const response = await fetch(uploadUrl, {
          method: 'POST',
          headers: { 'Content-Type': file.type },
          body: file,
        });
        const { storageId } = (await response.json()) as { storageId: Id<'_storage'> }; // AS_JUSTIFIED: Convex upload response is always { storageId: Id<'_storage'> }
        await onUpload(dependantId, storageId, file.name, 'document');
        toast.success('Document uploaded');
      } catch {
        toast.error('Upload failed');
      } finally {
        setIsUploading(false);
        if (inputRef.current) inputRef.current.value = '';
      }
    },
    [createUploadUrl, dependantId, onUpload]
  );

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={e => {
          void handleChange(e);
        }}
        accept=".pdf,.jpg,.jpeg,.png,.webp"
      />
      <Button
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs"
        disabled={isUploading}
        onClick={() => inputRef.current?.click()}
      >
        <Upload className="w-3 h-3 mr-1" aria-hidden="true" />
        {isUploading ? 'Uploading...' : 'Upload Doc'}
      </Button>
    </>
  );
}

export function DependantCard({
  dependant,
  onEdit,
  onRemove,
  onUploadDocument,
  onRemoveDocument,
}: IDependantCardProps) {
  const [confirmRemoveDoc, setConfirmRemoveDoc] = useState<Id<'_storage'> | null>(null);

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="font-medium text-sm text-foreground truncate">{dependant.name}</span>
          <span className="text-xs text-muted-foreground">
            {RELATIONSHIP_LABELS[dependant.relationship]}
            {dependant.dateOfBirth && ` · ${dependant.dateOfBirth}`}
            {dependant.memberId && ` · ID: ${dependant.memberId}`}
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => onEdit(dependant)}
            aria-label={`Edit ${dependant.name}`}
          >
            <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-destructive hover:text-destructive"
            onClick={() => onRemove(dependant._id)}
            aria-label={`Remove ${dependant.name}`}
          >
            <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
          </Button>
        </div>
      </div>

      {dependant.documents.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1">
          {dependant.documents.map(doc => (
            <span
              key={doc.storageId}
              className="inline-flex items-center gap-1 text-xs bg-muted px-2 py-0.5 rounded-full"
            >
              {doc.fileName}
              <button
                onClick={() => setConfirmRemoveDoc(doc.storageId)}
                aria-label={`Remove ${doc.fileName}`}
                className="text-muted-foreground hover:text-destructive transition-colors"
              >
                <X className="w-3 h-3" aria-hidden="true" />
              </button>
            </span>
          ))}
        </div>
      )}

      <DependantDocumentUpload dependantId={dependant._id} onUpload={onUploadDocument} />

      <AlertDialog
        open={confirmRemoveDoc !== null}
        onOpenChange={open => {
          if (!open) setConfirmRemoveDoc(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove document?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={() => {
                if (confirmRemoveDoc) {
                  void onRemoveDocument(dependant._id, confirmRemoveDoc);
                  setConfirmRemoveDoc(null);
                }
              }}
            >
              Remove
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
